import { useState } from "react";
import type { ScopeLevel, YesNoUnknown } from "@reno-estimator/core";
import { StepShell } from "../ui/StepShell.js";
import { OptionCard } from "../ui/OptionCard.js";
import { TriStateToggle } from "../ui/TriStateToggle.js";
import { getUnansweredRequiredProjectQuestions, ProjectSpecificQuestions } from "./details/ProjectSpecificQuestions.js";
import type { EstimatorFlow } from "../../state/useEstimatorFlow.js";

const SCOPE_OPTIONS: { id: ScopeLevel; label: string; description: string }[] = [
  { id: "cosmetic_refresh", label: "Cosmetic refresh", description: "Paint, fixtures, surfaces - no layout changes." },
  { id: "mid_range_remodel", label: "Mid-range remodel", description: "Some layout or system changes, updated finishes." },
  { id: "full_gut_renovation", label: "Full gut renovation", description: "Down to studs, full layout freedom." },
  { id: "custom_high_end", label: "Custom / high-end", description: "Bespoke design, high-end materials and finishes." }
];

export function Step3WhatChanging({ flow }: { flow: EstimatorFlow }) {
  const [moreOpen, setMoreOpen] = useState(false);

  const scope = (flow.getAnswer("scope") as ScopeLevel | undefined) ?? "mid_range_remodel";
  const accessibilityRequired = flow.getAnswer("accessibility_required") as YesNoUnknown | undefined;
  const needsDesign = flow.getAnswer("needs_design_services") as boolean | undefined;
  const permitLikely = flow.getAnswer("permit_likely_required") as YesNoUnknown | undefined;
  const requiredUnanswered = getUnansweredRequiredProjectQuestions(flow);

  return (
    <StepShell
      title="What are you changing?"
      helpText="This is the biggest driver of your estimate - tell us the scope, then anything structural, mechanical, or accessibility-related."
      onBack={flow.goBack}
      onNext={flow.goNext}
      nextDisabled={requiredUnanswered.length > 0}
    >
      <fieldset className="detail-field">
        <legend>Overall scope</legend>
        <div className="option-grid" role="radiogroup" aria-label="Renovation scope">
          {SCOPE_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.id}
              name="scope"
              value={opt.id}
              label={opt.label}
              description={opt.description}
              checked={scope === opt.id}
              onChange={(checked) => checked && flow.setAnswer("scope", opt.id)}
            />
          ))}
        </div>
      </fieldset>

      <div className="tri-toggle-grid">
        <TriStateToggle
          legend="Moving or adding plumbing?"
          value={flow.getAnswer("plumbing_changes") as YesNoUnknown | undefined}
          onChange={(v) => flow.setAnswer("plumbing_changes", v)}
        />
        <TriStateToggle
          legend="Moving or adding electrical?"
          value={flow.getAnswer("electrical_changes") as YesNoUnknown | undefined}
          onChange={(v) => flow.setAnswer("electrical_changes", v)}
        />
        <TriStateToggle
          legend="Changing walls or structure?"
          value={flow.getAnswer("structural_changes") as YesNoUnknown | undefined}
          onChange={(v) => flow.setAnswer("structural_changes", v)}
        />
        <TriStateToggle
          legend="Demolition required?"
          value={flow.getAnswer("demolition_required") as YesNoUnknown | undefined}
          onChange={(v) => flow.setAnswer("demolition_required", v)}
        />
      </div>

      <fieldset className="detail-field">
        <TriStateToggle
          legend="Accommodating accessibility or mobility needs?"
          helpText="For example: a curbless shower, widened doorways, grab bars, lever hardware, or lowered counters."
          value={accessibilityRequired}
          onChange={(v) => flow.setAnswer("accessibility_required", v)}
        />
        {accessibilityRequired === "yes" || accessibilityRequired === "unknown" ? (
          <label className="text-field">
            <span>Anything specific we should know? (optional)</span>
            <input
              type="text"
              value={(flow.getAnswer("accessibility_notes") as string | undefined) ?? ""}
              onChange={(e) => flow.setAnswer("accessibility_notes", e.target.value)}
            />
          </label>
        ) : null}
      </fieldset>

      <ProjectSpecificQuestions flow={flow} />

      {requiredUnanswered.length > 0 ? (
        <p className="step__help" role="status">
          Answer {requiredUnanswered.map((q) => `"${q.prompt}"`).join(" and ")} above to continue.
        </p>
      ) : null}

      <div className="more-details">
        <button
          type="button"
          className="btn btn--link"
          aria-expanded={moreOpen}
          aria-controls="more-details-panel"
          onClick={() => setMoreOpen((v) => !v)}
        >
          {moreOpen ? "Hide" : "Show"} design & permit details (optional)
        </button>
        {moreOpen ? (
          <div id="more-details-panel" className="more-details__panel">
            <fieldset className="detail-field">
              <legend>Do you want design services included?</legend>
              <div className="option-grid option-grid--compact">
                <OptionCard
                  name="needs_design_services"
                  value="yes"
                  label="Yes, include design services"
                  description="An interior designer or drafter"
                  type="checkbox"
                  checked={needsDesign === true}
                  onChange={(checked) => flow.setAnswer("needs_design_services", checked)}
                />
              </div>
            </fieldset>
            <TriStateToggle
              legend="Do you know if a permit is required?"
              helpText="If unsure, we'll flag this as something to confirm with your municipality."
              value={permitLikely}
              onChange={(v) => flow.setAnswer("permit_likely_required", v)}
            />
          </div>
        ) : null}
      </div>
    </StepShell>
  );
}
